import React, { useRef, useEffect } from 'react'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Text } from '../../atoms/Text'
import { Button } from '../../atoms/Button'
import {
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
  DetailPanelFooter,
  PanelZoomButton,
} from '../ResizableDetailPanel'
import { permitZoomTarget } from '../ResizableDetailPanel/panelZoomTarget'
import { BuildingPermitDetailProps } from './BuildingPermitDetail.types'
import { BuildingPermitDetailContent } from './BuildingPermitDetailContent'
import * as S from './BuildingPermitDetail.styles'

export const BuildingPermitDetail: React.FC<BuildingPermitDetailProps> = ({
  isOpen,
  permit,
  loading = false,
  error = null,
  onClose,
  formatAddress,
  getTotalLandArea,
  getPrimaryOfficialId,
  getActiveRegblFlags,
  getParticipantsByRoleGrouped,
  formatLandArea,
  resolvedLands,
  landsLoading,
  onViewLand,
  landDetailContent,
  landDetailOpen = false,
  onLandDetailClose,
  contained,
  disableResize = false,
  printMode = false,
  isSaved = false,
  onToggleSave,
  onAddToDeal,
  onZoom,
  zoomLabel = 'Zoom',
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const contentRef = useRef<HTMLDivElement>(null)

  // Scroll to top when switching between permit and land views
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [landDetailOpen])

  // In print mode, render just the content without the panel wrapper
  if (printMode) {
    return (
      <BuildingPermitDetailContent
        permit={permit}
        loading={loading}
        error={error}
        formatAddress={formatAddress}
        getTotalLandArea={getTotalLandArea}
        getPrimaryOfficialId={getPrimaryOfficialId}
        getActiveRegblFlags={getActiveRegblFlags}
        getParticipantsByRoleGrouped={getParticipantsByRoleGrouped}
        formatLandArea={formatLandArea}
        resolvedLands={resolvedLands}
        landsLoading={landsLoading}
        onViewLand={onViewLand}
        printMode
      />
    )
  }

  return (
    <ResizableDetailPanel
      isOpen={isOpen}
      onClose={onClose}
      storageKey="building_permit_detail_panel_width"
      defaultWidth="900px"
      minWidth={400}
      maxWidthPercentage={0.8}
      contained={contained}
      disableResize={disableResize}
    >
      {landDetailOpen && landDetailContent ? (
        <>
          <DetailPanelHeader onClose={onClose}>
            <S.LandDetailBackButton onClick={onLandDetailClose}>
              <Icon name="arrow-left" size="small" />
              {t('buildingPermits.detail.buttons.backToPermit')}
            </S.LandDetailBackButton>
          </DetailPanelHeader>
          <DetailPanelContent ref={contentRef}>{landDetailContent}</DetailPanelContent>
        </>
      ) : (
        <>
          <DetailPanelHeader onClose={onClose}>
            <S.DetailHeaderBar>
              <S.DetailHeaderInfo>
                <Text variant="caption" color="secondary">
                  {t('buildingPermits.detail.type')}
                </Text>
              </S.DetailHeaderInfo>
              {!printMode && (onZoom || onToggleSave) && (
                <S.HeaderActions>
                  {onZoom && permitZoomTarget(permit) && (
                    <PanelZoomButton
                      label={zoomLabel}
                      onClick={() => {
                        const position = permitZoomTarget(permit)
                        if (position) onZoom(position)
                      }}
                    />
                  )}
                  {onToggleSave && (
                    <IconButton
                      variant={isSaved ? 'solid' : 'default'}
                      aria-label={t('common.save')}
                      onClick={onToggleSave}
                    >
                      <Icon
                        name="bookmark"
                        size="medium"
                        color={isSaved ? theme.colors.primary : undefined}
                        fill={isSaved ? theme.colors.primary : 'transparent'}
                      />
                    </IconButton>
                  )}
                </S.HeaderActions>
              )}
            </S.DetailHeaderBar>
          </DetailPanelHeader>
          <DetailPanelContent>
            <BuildingPermitDetailContent
              permit={permit}
              loading={loading}
              error={error}
              formatAddress={formatAddress}
              getTotalLandArea={getTotalLandArea}
              getPrimaryOfficialId={getPrimaryOfficialId}
              getActiveRegblFlags={getActiveRegblFlags}
              getParticipantsByRoleGrouped={getParticipantsByRoleGrouped}
              formatLandArea={formatLandArea}
              resolvedLands={resolvedLands}
              landsLoading={landsLoading}
              onViewLand={onViewLand}
            />
          </DetailPanelContent>

          {onAddToDeal && !printMode && (
            <DetailPanelFooter>
              <Button variant="secondary" onClick={() => permit && onAddToDeal?.(permit)}>
                <Icon name="git-branch" size="small" />
                Add to Deal
              </Button>
            </DetailPanelFooter>
          )}
        </>
      )}
    </ResizableDetailPanel>
  )
}
